// ---------------------------------------------------------------------------
// UX-009: PWA install-prompt hook
// ---------------------------------------------------------------------------
// Captures the browser-fired `beforeinstallprompt` event so the UI can decide
// when (and how) to surface an "install app" affordance instead of relying on
// the default mini-infobar. Returns a stable, declarative API:
//
//   const { isInstallable, promptInstall } = useInstallPrompt();
//
// - `isInstallable`  becomes true once the browser has signalled the app is
//                    eligible for installation.
// - `promptInstall`  triggers the native install prompt and resolves with the
//                    user's choice ("accepted" | "dismissed" | "unavailable").
//
// Also listens for `appinstalled` to clear state once the app is installed,
// matching the W3C "Web App Manifest" spec.

import { useCallback, useEffect, useRef, useState } from "react";
import type { BeforeInstallPromptEvent } from "@/lib/pwa";

export type InstallPromptOutcome = "accepted" | "dismissed" | "unavailable";

export interface UseInstallPromptResult {
  readonly isInstallable: boolean;
  readonly promptInstall: () => Promise<InstallPromptOutcome>;
}

export function useInstallPrompt(): UseInstallPromptResult {
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      deferredPromptRef.current = event as BeforeInstallPromptEvent;
      setIsInstallable(true);
    };

    const handleInstalled = () => {
      deferredPromptRef.current = null;
      setIsInstallable(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<InstallPromptOutcome> => {
    const deferred = deferredPromptRef.current;
    if (!deferred) {
      return "unavailable";
    }

    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      return choice.outcome;
    } catch {
      return "unavailable";
    } finally {
      deferredPromptRef.current = null;
      setIsInstallable(false);
    }
  }, []);

  return { isInstallable, promptInstall };
}
